import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Radio, Calendar, PlayCircle, Share2, Bell, ExternalLink, Gift, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;
const publicClient = axios.create({ withCredentials: false });

const STATUS_LABEL = {
  draft: "Draft",
  scheduled: "Scheduled",
  live: "LIVE NOW",
  replay: "Replay Available",
  ended: "Ended",
};

const STATUS_COLOR = {
  scheduled: "bg-[#3B82F6]/15 text-[#3B82F6] border-[#3B82F6]/30",
  live: "bg-[#EF4444]/15 text-[#EF4444] border-[#EF4444]/40 animate-pulse",
  replay: "bg-[#10B981]/15 text-[#10B981] border-[#10B981]/30",
  ended: "bg-white/5 text-[#94A3B8] border-white/10",
};

export default function LiveEventsSection({ listing }) {
  const [events, setEvents] = useState(null);

  useEffect(() => {
    if (!listing?.slug) return;
    let cancelled = false;
    publicClient
      .get(`${API}/live-events`, { params: { listing_slug: listing.slug }, headers: { Authorization: "" } })
      .then((r) => { if (!cancelled) setEvents(r.data.events || []); })
      .catch(() => { if (!cancelled) setEvents([]); });
    return () => { cancelled = true; };
  }, [listing?.slug]);

  if (!events || events.length === 0) return null;

  return (
    <section className="mt-10" data-testid="live-events-public-section">
      <div className="flex items-center gap-2 mb-4">
        <Radio className="w-5 h-5 text-[#EF4444]" />
        <h2 className="text-xl sm:text-2xl font-bold text-white font-['Outfit']">Live Events & Replays</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {events.map((ev) => <LiveEventCard key={ev.id} ev={ev} listingSlug={listing.slug} />)}
      </div>
    </section>
  );
}

function LiveEventCard({ ev, listingSlug }) {
  const status = ev.status;
  const eventUrl = `/live/${listingSlug}/${ev.event_slug}`;
  return (
    <div className={`relative rounded-2xl border ${STATUS_COLOR[status] || "border-white/10"} bg-[#0F172A] overflow-hidden min-w-0`} data-testid={`live-event-card-${ev.event_slug}`}>
      {ev.image_url && (
        <Link to={eventUrl} className="block relative">
          <img src={ev.image_url} alt={ev.title} className="w-full h-44 object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-transparent to-transparent pointer-events-none" />
          <span className={`absolute top-3 left-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold border ${STATUS_COLOR[status] || "border-white/20 bg-black/40 text-white"}`}>
            {status === "live" && <Radio className="w-3 h-3" />} {STATUS_LABEL[status]}
          </span>
        </Link>
      )}
      <div className="p-4">
        {!ev.image_url && (
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 mb-2 rounded-full text-[10px] uppercase tracking-wider font-bold border ${STATUS_COLOR[status] || "border-white/20 bg-black/40 text-white"}`}>
            {status === "live" && <Radio className="w-3 h-3" />} {STATUS_LABEL[status]}
          </span>
        )}
        <Link to={eventUrl} className="block">
          <p className="text-white font-bold text-base sm:text-lg leading-tight hover:text-[#FBBF24] transition">{ev.title}</p>
        </Link>
        {ev.starts_at && (
          <p className="text-xs text-[#94A3B8] mt-1.5 flex items-center gap-1">
            <Calendar className="w-3 h-3" /> {new Date(ev.starts_at).toLocaleString()}
          </p>
        )}
        {status === "scheduled" && ev.starts_at && <Countdown to={ev.starts_at} />}

        <div className="mt-4 flex flex-wrap gap-2">
          {status === "live" && (
            <Link to={eventUrl} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-gradient-to-r from-[#EF4444] to-[#EC4899] text-white text-xs font-semibold" data-testid={`watch-live-${ev.event_slug}`}>
              <PlayCircle className="w-3.5 h-3.5" /> Watch Live
            </Link>
          )}
          {status === "scheduled" && (
            <>
              <Link to={eventUrl} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white text-xs font-semibold">
                <Bell className="w-3.5 h-3.5" /> Get Reminder
              </Link>
              <AddToCalendar ev={ev} />
            </>
          )}
          {status === "replay" && ev.replay_url && (
            <Link to={eventUrl} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-gradient-to-r from-[#10B981] to-[#3B82F6] text-white text-xs font-semibold" data-testid={`watch-replay-${ev.event_slug}`}>
              <PlayCircle className="w-3.5 h-3.5" /> Watch Replay
            </Link>
          )}
          {ev.offer_text && (
            <Link to={eventUrl} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#FBBF24]/15 border border-[#FBBF24]/30 text-[#FBBF24] text-xs font-semibold">
              <Gift className="w-3.5 h-3.5" /> Claim Offer
            </Link>
          )}
          <ShareBtn url={typeof window !== "undefined" ? `${window.location.origin}${eventUrl}` : eventUrl} title={ev.title} />
        </div>
      </div>
    </div>
  );
}

function Countdown({ to }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, new Date(to).getTime() - now);
  if (diff <= 0) return null;
  const s = Math.floor(diff / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  return (
    <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-white tabular-nums" data-testid="event-countdown">
      <Sparkles className="w-3 h-3 text-[#FBBF24]" />
      {d > 0 && <span>{d}d</span>}<span>{String(h).padStart(2, "0")}:{String(m).padStart(2, "0")}:{String(ss).padStart(2, "0")}</span>
    </div>
  );
}

function AddToCalendar({ ev }) {
  const onClick = () => {
    const start = ev.starts_at ? new Date(ev.starts_at) : new Date();
    const end = ev.ends_at ? new Date(ev.ends_at) : new Date(start.getTime() + 60 * 60 * 1000);
    const fmt = (d) => d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
    const params = new URLSearchParams({
      action: "TEMPLATE",
      text: ev.title,
      details: ev.description || "",
      dates: `${fmt(start)}/${fmt(end)}`,
    });
    window.open(`https://calendar.google.com/calendar/render?${params.toString()}`, "_blank");
  };
  return (
    <button onClick={onClick} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white text-xs font-semibold" data-testid="add-to-calendar-btn">
      <Calendar className="w-3.5 h-3.5" /> Add to Calendar
    </button>
  );
}

function ShareBtn({ url, title }) {
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title, url }); return; } catch (_) { /* user cancelled */ }
    }
    try { await navigator.clipboard.writeText(url); } catch (_) {}
  };
  return (
    <button onClick={share} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white text-xs font-semibold">
      <Share2 className="w-3.5 h-3.5" /> Share
    </button>
  );
}
